<template>
    <form>
        <LoaderProfile v-if="isFetching" :isFull="true"></LoaderProfile>
        <template v-else>
            <div class="d-flex justify-content-between mb-4">
                <div>
                    <h1 class="mb-0">{{ title }}</h1>
                </div>
                <div>
                    <button
                        type="button"
                        class="btn mr-2 cancel-btn"
                        @click="cancel"
                    >
                        Cancel
                    </button>
                    <button type="button" class="btn save-btn" @click="save">
                        Save
                    </button>
                </div>
            </div>
            <div class="row">
                <div class="col-xl-12 col-md-12 col-sm-12">
                    <div
                        v-if="showLimitWarningMessage"
                        class="alert alert-warning m-0 mb-3"
                        role="alert"
                    >
                        Each report can have a maximum of 2 scheduled reports.
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-xl-6 col-md-6 col-sm-12">
                    <div class="form-group form-row">
                        <label for="report" class="col-sm-4 col-form-label"
                            >Report:</label
                        >
                        <div class="col-sm align-content-around">
                            <span v-if="isEditMode">
                                {{ editedReportReportName }}
                            </span>
                            <treeselect
                                v-else
                                id="report"
                                ref="vreport"
                                name="report"
                                :options="reportOptions"
                                v-model="data.report"
                                placeholder="Select Report (required)"
                                v-validate="'required'"
                                :disable-branch-nodes="true"
                                :normalizer="normalizer"
                                :class="reportClass"
                                :defaultExpandLevel="Infinity"
                                :valueFormat="'object'"
                                :clearable="true"
                            >
                                <template
                                    slot="option-label"
                                    slot-scope="{ node }"
                                >
                                    <label
                                        v-if="node.isBranch"
                                        class="font-weight-bold"
                                    >
                                        {{ node.label }}
                                    </label>
                                    <label v-else class="font-weight-normal">
                                        {{ node.label }}
                                    </label>
                                </template>
                            </treeselect>
                        </div>
                    </div>

                    <div class="form-group form-row">
                        <label
                            for="scheduled_report_name"
                            class="col-sm-4 col-form-label"
                            >Name:</label
                        >
                        <div class="col-sm align-content-around">
                            <input
                                id="scheduled_report_name"
                                class="form-control"
                                v-model="data.scheduled_report_name"
                                v-validate="'required'"
                                name="scheduled_report_name"
                                :disabled="disabledFields"
                                placeholder="Name (required)"
                                :class="scheduledReportNameClass"
                            />
                        </div>
                    </div>

                    <!-- <div class="form-group form-row">
                        <label for="export" class="col-sm-4 col-form-label"
                            >Export:</label
                        >
                        <div class="col-sm">
                            <treeselect
                                id="export"
                                ref="vexport"
                                name="export"
                                :options="exportOptions"
                                v-model="data.export"
                                placeholder="Select export (required)"
                                v-validate="'required'"
                                multiple
                                :normalizer="normalizer"
                                :class="exportClass"
                                :defaultExpandLevel="Infinity"
                                :valueFormat="'id'"
                                :clearable="false"
                                :searchable="false"
                                :value-consists-of="'BRANCH_PRIORITY'"
                            >
                                <template
                                    slot="option-label"
                                    slot-scope="{ node }"
                                >
                                    <label
                                        v-if="node.isBranch"
                                        class="font-weight-normal"
                                    >
                                        {{ node.label }}
                                    </label>
                                    <label v-else class="font-weight-normal">
                                        {{ node.label }}
                                    </label>
                                </template>
                            </treeselect>
                        </div>
                    </div> -->

                    <div class="form-group form-row">
                        <label for="export_type" class="col-sm-4 col-form-label"
                            >Export Type:</label
                        >
                        <div class="col-sm">
                            <select
                                id="export_type"
                                class="form-control"
                                v-model="data.export_type"
                                v-validate="'required'"
                                name="export_type"
                                :class="exportTypeClass"
                                :disabled="disabledFields"
                            >
                                <option disabled :value="null"
                                    >Select Export Type</option
                                >
                                <option
                                    v-for="opt in exportTypeOptions"
                                    :key="opt.key"
                                    :value="opt.key"
                                >
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group form-row" v-if="isShowExportScope">
                        <label
                            for="export_scope"
                            class="col-sm-4 col-form-label"
                            >Export:</label
                        >
                        <div class="col-sm">
                            <select
                                id="export_scope"
                                class="form-control"
                                v-model="data.export_scope"
                                v-validate="'required'"
                                name="export_scope"
                                :class="exportScopeClass"
                                :disabled="disabledFields"
                            >
                                <option disabled :value="null"
                                    >Select Export</option
                                >
                                <option
                                    v-for="opt in exportScopeOptions"
                                    :key="opt.key"
                                    :value="opt.key"
                                >
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group form-row" v-if="isShowDateRange">
                        <label for="date_range" class="col-sm-4 col-form-label"
                            >Date Range:</label
                        >
                        <div class="col-sm">
                            <select
                                id="date_range"
                                class="form-control"
                                v-model="data.date_range"
                                v-validate="'required'"
                                name="date_range"
                                :class="dateRangeClass"
                                :disabled="disabledFields"
                            >
                                <option disabled :value="null"
                                    >Select Date Range</option
                                >
                                <option
                                    v-for="opt in dateRangeOptions"
                                    :key="opt.code"
                                    :value="opt.code"
                                    >{{ opt.label }}</option
                                >
                            </select>
                        </div>
                    </div>

                    <div class="form-group form-row">
                        <label for="recurring" class="col-sm-4 col-form-label"
                            >Recurring:</label
                        >
                        <div class="col-sm">
                            <select
                                id="recurring"
                                class="form-control"
                                v-model="data.recurring"
                                v-validate="'required'"
                                name="recurring"
                                :class="recurringClass"
                                :disabled="disabledFields"
                            >
                                <option disabled :value="null"
                                    >Select Recurring</option
                                >
                                <option
                                    v-for="opt in RECURRING_OPTION"
                                    :key="opt.value"
                                    :value="opt.value"
                                    >{{ opt.label }}</option
                                >
                            </select>
                        </div>
                    </div>

                    <div class="form-group form-row">
                        <label for="report_date" class="col-sm-4 col-form-label"
                            >Start Date:</label
                        >
                        <div class="col-sm">
                            <date-picker
                                :config="datepickerConfig"
                                v-model="data.report_date"
                                placeholder="Start date (required)"
                                v-validate="'required'"
                                name="reportDate"
                                :class="reportDateClass"
                                :disabled="disabledFields"
                            />
                        </div>
                    </div>

                    <div class="form-group form-row">
                        <label for="send_type" class="col-sm-4 col-form-label"
                            >Send via:</label
                        >
                        <div class="col-sm">
                            <select
                                id="send_type"
                                class="form-control"
                                v-model="data.send_type"
                                v-validate="'required'"
                                name="send_type"
                                :class="sendTypeClass"
                                placeholder="Select Send via"
                                :disabled="disabledFields"
                            >
                                <option
                                    v-for="opt in sendTypeOptions"
                                    :key="opt.value"
                                    :value="opt.value"
                                >
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group form-row">
                        <label for="recipients" class="col-sm-4 col-form-label"
                            >Recipients:</label
                        >
                        <div class="col-sm">
                            <v-select
                                id="recipients"
                                ref="recipients"
                                name="recipients"
                                :options="filteredRecipientOptions"
                                v-model="data.recipients"
                                :label="'fullname'"
                                placeholder="Recipients (required)"
                                v-validate="'required'"
                                :class="recipientsClass"
                                multiple
                                :disabled="disabledFields"
                            />
                        </div>
                    </div>
                </div>

                <div class="col-xl-6 col-md-6 col-sm-12">
                    <!-- Filter Section -->

                    <div v-if="data.report">
                        <SelectItemClassCategory
                            ref="selectItemClassCategory"
                            v-if="
                                !isEditMode ||
                                (data.report.isShowItemClass ||
                                    data.report.isShowItemCategory)
                            "
                            v-show="
                                data.report.isShowItemClass ||
                                data.report.isShowItemCategory
                            "
                            :label-cols="4"
                            :data="data.filters"
                            @update-data="handleChildChanges"
                            :isShowItemCategory="data.report.isShowItemCategory"
                            :isShowItemClass="data.report.isShowItemClass"
                        />
                        <SelectProvider
                            v-if="
                                !isEditMode || data.report.isShowProviderOptions
                            "
                            v-show="data.report.isShowProviderOptions"
                            :data="data.filters"
                            :label-cols="4"
                            @update-data="handleChildChanges"
                        />

                        <CategoryWithSales
                            v-show="data.report.isShowCategorySales"
                            :dataCategorySales="defaultCategorySales"
                            :label-cols="4"
                            @update-data="handleChildChanges"
                        />

                        <ShowAllCategoriesCheckbox
                            v-show="
                                data.report.reportId ===
                                    CREPORT.TYPE
                                        .PRODUCTION_CREDIT_BY_PAYMENTS ||
                                data.report.reportId ===
                                    CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                            "
                            :reportId="data.report.reportId"
                            :value="
                                data.filters &&
                                data.filters.isShowAllSalesCategory
                            "
                            :label-cols="4"
                            @update-data="handleChildChanges"
                        />

                        <IncludeRefillSalesCheckbox
                            v-show="
                                data.report.reportId ===
                                    CREPORT.TYPE
                                        .PRODUCTION_CREDIT_BY_PAYMENTS ||
                                data.report.reportId ===
                                    CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                            "
                            :value="
                                data.filters &&
                                data.filters.isIncludeRefillSales
                            "
                            :label-cols="4"
                            @update-data="handleChildChanges"
                        />

                        <ExcludeEligibilityFeeCheckbox
                            v-show="
                                data.report.reportId ===
                                    CREPORT.TYPE
                                        .PRODUCTION_CREDIT_BY_PAYMENTS ||
                                data.report.reportId ===
                                    CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                            "
                            :value="
                                data.filters &&
                                data.filters.isExcludeEligibilityFee
                            "
                            :tooltipMessage="TOOLTIP.MESSAGE.PROD_CREDIT"
                            :label-cols="4"
                            @update-data="handleChildChanges"
                        />
                    </div>
                </div>
            </div>
        </template>
    </form>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import { differenceBy } from "lodash";
import CREPORT from "@/constants/report";
import ReportService from "@/services/report.js";
import StaffService from "@/services/staff";
import TOOLTIP from "@/constants/tooltip-message";
import ScheduledReportService from "@/services/report/scheduled";
import LoaderProfile from "@/components/elements/loader-profile.vue";
import REPORT_PERMISSION from "@/constants/report-permission";
import vSelect from "vue-select";
import { EXPORT_TYPE, EXPORT_SCOPE } from "@/constants/report";
import { REPORT_FILTERING } from "@/constants/report-filtering";

const RECURRING_DAILY = 1;
const RECURRING_WEEKDAY = 2;
const RECURRING_WEEKLY = 3;
const RECURRING_MONTHLY = 4;
const RECURRING_YEARLY = 5;
const RECURRING_OPTION = [
    {
        label: "Daily",
        value: RECURRING_DAILY
    },
    {
        label: "Weekday",
        value: RECURRING_WEEKDAY
    },
    {
        label: "Weekly",
        value: RECURRING_WEEKLY
    },
    {
        label: "Monthly",
        value: RECURRING_MONTHLY
    },
    {
        label: "Yearly",
        value: RECURRING_YEARLY
    }
];

const SEND_TYPE_BOTH = 0;
const SEND_TYPE_EMAIL = 1;
const SEND_TYPE_NOTIFICATION = 2;

export default {
    components: {
        LoaderProfile,
        Treeselect,
        vSelect,
        SelectProvider: () =>
            import("@/module/report/module/filter-fields/select-provider.vue"),
        SelectItemClassCategory: () =>
            import(
                "@/module/report/module/filter-fields/select-item-class-category.vue"
            ),
        CategoryWithSales: () =>
            import(
                "@/module/report/module/filter-fields/category-with-sales.vue"
            ),
        ShowAllCategoriesCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/show-all-categories-checkbox.vue"
            ),
        IncludeRefillSalesCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/include-refill-sales-checkbox.vue"
            ),
        ExcludeEligibilityFeeCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/exclude-eligibility-fee-checkbox.vue"
            )
    },
    data() {
        return {
            RECURRING_OPTION: RECURRING_OPTION,
            isSaving: false,
            isFetching: true,
            editedReport: null,
            showLimitWarningMessage: false,
            data: {
                id: null,
                report: null,
                scheduled_report_name: null,
                export_type: null,
                export_scope: null,
                date_range: null,
                filters: null,
                recurring: null,
                report_date: null,
                recipients: [],
                send_type: SEND_TYPE_BOTH
            },
            reportOptions: [],
            sendTypeOptions: [
                { value: SEND_TYPE_BOTH, label: "Both Email and Notification" },
                { value: SEND_TYPE_EMAIL, label: "Email" },
                { value: SEND_TYPE_NOTIFICATION, label: "Notification" }
            ],
            recipientOptions: [],
            CREPORT: CREPORT,
            TOOLTIP: TOOLTIP,
            disabledReportIds: []
        };
    },
    computed: {
        // #region Form Validation Classes
        exportClass() {
            return {
                "treeselect-invalid": this.errors.has("export")
            };
        },
        scheduledReportNameClass() {
            return {
                "border-danger": this.errors.has("scheduled_report_name")
            };
        },
        exportTypeClass() {
            return {
                "border-danger": this.errors.has("export_type")
            };
        },
        exportScopeClass() {
            return {
                "border-danger": this.errors.has("export_scope")
            };
        },
        dateRangeClass() {
            return {
                "border-danger": this.errors.has("date_range")
            };
        },
        recurringClass() {
            return {
                "border-danger": this.errors.has("recurring")
            };
        },
        reportDateClass() {
            return {
                "border-danger": this.errors.has("reportDate")
            };
        },
        sendTypeClass() {
            return {
                "border-danger": this.errors.has("send_type")
            };
        },
        recipientsClass() {
            return {
                "border-danger": this.errors.has("recipients")
            };
        },
        reportClass() {
            return {
                "treeselect-invalid": this.errors.has("report")
            };
        },
        // #endregion
        disabledFields() {
            return this.data.report === null;
        },
        editedReportReportName() {
            return this.editedReport?.reportName;
        },
        isShowExportScope() {
            if (this.data.report) {
                return (
                    this.data.report.export_scopes?.length > 0 &&
                    this.isExportTypeCsv
                );
            }
            return true;
        },
        isShowDateRange() {
            if (this.data.report) {
                return this.data.report.isShowDateRange;
            }
            return true;
        },
        isExportTypeCsv() {
            return [EXPORT_TYPE.CSV, EXPORT_TYPE.BOTH].includes(
                this.data.export_type
            );
        },

        filteredRecipientOptions() {
            let filteredOptions = this.recipientOptions;

            if (this.data.report) {
                if (!this.data.report.isCustom) {
                    // Custom dont have permission
                    filteredOptions = this.recipientOptions.filter(
                        recipient => {
                            if (recipient.issuper) {
                                return true;
                            }

                            const permissionSet = new Set(
                                recipient.report_permissions
                            );

                            if (
                                permissionSet.has("report.access") &&
                                permissionSet.has(
                                    REPORT_PERMISSION.REPORT_PERMISSIONS[
                                        this.data.report.reportId
                                    ]
                                )
                            ) {
                                return true;
                            }

                            return false;
                        }
                    );
                }
            }

            filteredOptions = differenceBy(
                filteredOptions,
                this.data.recipients,
                "id"
            );
            return filteredOptions;
        },
        datepickerConfig() {
            return {
                format: this.getDateFormat(),
                minDate: moment().add(1, "days").startOf("day")
            };
        },
        routeId() {
            return this.$route.params.id;
        },
        isEditMode() {
            return this.routeId != null;
        },
        title() {
            return this.isEditMode
                ? "Edit Scheduled Report"
                : "New Scheduled Report";
        },
        exportTypeOptions() {
            if (this.data.report?.exportOptionCsvOnly) {
                return [
                    {
                        key: EXPORT_TYPE.CSV,
                        label: "CSV"
                    }
                ];
            } else {
                return [
                    {
                        key: EXPORT_TYPE.BOTH,
                        label: "Both PDF and CSV"
                    },
                    {
                        key: EXPORT_TYPE.CSV,
                        label: "CSV"
                    },
                    {
                        key: EXPORT_TYPE.PDF,
                        label: "PDF"
                    }
                ];
            }
        },
        exportScopeOptions() {
            const exportScopeOptions = [
                {
                    key: EXPORT_SCOPE.SUMMARY,
                    label: "Summary"
                },
                {
                    key: EXPORT_SCOPE.DETAILS,
                    label: "Details"
                },
                {
                    key: EXPORT_SCOPE.COMPLETE,
                    label: "Complete"
                }
            ];

            if (this.data.report?.export_scopes) {
                return exportScopeOptions.filter(scope =>
                    this.data.report.export_scopes.includes(scope.key)
                );
            }
            return exportScopeOptions;
        },
        exportOptions() {
            const report = this.data.report;
            const hasExportScopes = report?.export_scopes?.length > 0;

            // Build CSV children dynamically from report export_scopes
            const csvChildren = hasExportScopes
                ? report.export_scopes.map(scope => ({
                      id: scope,
                      label: scope
                  }))
                : [];

            const csvOption = {
                id: EXPORT_TYPE.CSV,
                label: "CSV",
                ...(csvChildren.length > 0 && { children: csvChildren })
            };

            return [
                {
                    id: EXPORT_TYPE.BOTH,
                    label: "Select both CSV and PDF",
                    children: [
                        csvOption,
                        {
                            id: EXPORT_TYPE.PDF,
                            label: "PDF"
                        }
                    ]
                }
            ];
        },
        dateRangeOptions() {
            return [
                {
                    label: "Yesterday",
                    code: "10"
                },
                {
                    label: "This week - The current week to date",
                    code: "2"
                },
                {
                    label: "This month - The current calendar month",
                    code: "3"
                },
                {
                    label: "This quarter - The current calendar quarter",
                    code: "4"
                },
                {
                    label: "This year - The current calendar year",
                    code: "5"
                },
                {
                    label: "Last week - The last calendar week",
                    code: "6"
                },
                {
                    label: "Last month - The last calendar month",
                    code: "7"
                },
                {
                    label: "Last quarter - The last calendar quarter",
                    code: "8"
                },
                {
                    label: "Last year - The last calendar year",
                    code: "9"
                }
            ];
        },
        defaultCategorySales() {
            return this.data.report.isShowCategorySales
                ? this.data?.filters?.categorySales ?? true
                : false;
        }
    },
    async mounted() {
        await this._getSetting();
        this.dynamicPageTitle("Scheduled Report");

        this.data.report_date = this._now()
            .plus({ days: 1 })
            .toFormat(this._dateFormat);

        this.loadReports();
        this.loadRecipients();
        if (!this.isEditMode) {
            this.reportCount();
            this.isFetching = false;
        }
    },
    watch: {
        async "data.report"(value) {
            if (value && !this.isEditMode) {
                this.resetForm();
                this.data.scheduled_report_name = value.name;
                if (value.isCustom) {
                    this.data.export_type = EXPORT_TYPE.CSV;
                    if (!value.loaded) {
                        this.loadCustomReport(value);
                    }
                }
            }
        }
    },
    methods: {
        save() {
            this.$emit("save", { addNew: false });
        },
        cancel() {
            this.$emit("cancel");
        },
        findReportInOptions(scheduledReport) {
            const targetId =
                scheduledReport.report_id || scheduledReport.custom_report_id;

            for (const category of this.reportOptions) {
                if (category.children) {
                    for (const report of category.children) {
                        if (report.reportId === targetId) {
                            return report;
                        }
                    }
                }
            }
            return null;
        },
        normalizer(node) {
            return {
                id: node.id,
                label: node.label || node.name,
                children: node.children,
                isDisabled: node.isDisabled || false,
                isDefaultExpanded: true
            };
        },
        resetForm() {
            this.$validator.pause();
            this.data.id = null;
            this.data.export_type = null;
            this.data.export_scope = null;
            this.data.date_range = null;
            this.data.filters = null;
            // this.data.recurring = null;
            // this.data.send_type = SEND_TYPE_BOTH;
            // this.data.report_date = null;
            this.data.recipients = [];
            this.$validator.reset();
            this.$validator.resume();
        },
        async loadCustomReport(customReport) {
            const response = await ReportService.getRenderCustomReport(
                customReport.reportId,
                {
                    is_initial_load: 1,
                    from: 0,
                    to: 0
                }
            );

            // Use Vue.set to make the property reactive
            this.$set(
                customReport,
                "isShowDateRange",
                response.resource.filter_options.date_range
            );
            this.$set(customReport, "loaded", true);
        },
        async reportCount() {
            const response = await ScheduledReportService.getCount();

            this.disabledReportIds = response.resource
                .filter(report => {
                    return report.count >= 2;
                })
                .map(report => {
                    let reportId = report.report_id;
                    if (!reportId) {
                        reportId = report.custom_report_id;
                    }
                    return reportId;
                });
            this.showLimitWarningMessage = this.disabledReportIds.length > 0;
            this.updateDisabledStates();
        },
        updateDisabledStates() {
            if (
                this.disabledReportIds.length === 0 ||
                this.reportOptions.length === 0
            ) {
                return;
            }

            this.reportOptions.forEach(category => {
                if (category.children) {
                    category.children.forEach(child => {
                        const isDisabled = this.disabledReportIds.includes(
                            child.reportId
                        );
                        this.$set(child, "isDisabled", isDisabled);
                    });
                }
            });
        },
        handleChildChanges(inputObject) {
            this.data.filters = {
                ...(this.data.filters || {}),
                ...inputObject
            };
        },
        async loadReports() {
            // Standard billing reports
            // If later we add more report from other module, then remove param module to render all reports
            const res = await ReportService.getReports(CREPORT.MODULE.BILLING);
            const reportsFromAPI = res.resources;
            const standardReports = REPORT_FILTERING.map(report => {
                const reportFromAPI = reportsFromAPI.find(
                    r => r.id === report.reportId
                );
                return {
                    ...report,
                    ...reportFromAPI
                };
            }).filter(report => report.isactive === true);

            const customReports = await this.initCustomReport();

            this.reportOptions = [
                {
                    id: "report-billing",
                    name: "Billing",
                    children: standardReports.sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                },
                {
                    id: "report-custom",
                    name: "Custom",
                    children: customReports.sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                }
            ];

            if (this.isEditMode) {
                this.loadEditedReport();
            }
            this.updateDisabledStates();
        },
        async initCustomReport() {
            let self = this;
            self.loaded = false;

            const response = await ReportService.getCustomReport();

            if (response) {
                return response.resources
                    .map(item => {
                        if (item.isactive && item.ispublish) {
                            return {
                                ...item,
                                isCustom: true,
                                reportId: item.id,
                                exportOptionCsvOnly: true,
                                loaded: false
                            };
                        }
                        return null; // or undefined
                    })
                    .filter(Boolean);
            }
            return [];
        },
        loadRecipients() {
            // Load clinic staff with permission
            let self = this;
            StaffService.getAllStaffWithReportPermission().then(response => {
                self.recipientOptions = response.resources;
            });
        },
        async validateForm() {
            const isValid = await this.$validator.validateAll();
            return isValid;
        },
        async loadEditedReport() {
            this.isFetching = true;
            const res = await ScheduledReportService.get(this.routeId);
            const scheduledReport = res?.resource || null;

            this.editedReport = scheduledReport;
            this.$set(
                this.editedReport,
                "reportName",
                scheduledReport.report_name ||
                    scheduledReport.custom_report_name
            );

            this.isFetching = false;

            if (scheduledReport) {
                this.data.id = scheduledReport.id;
                this.data.scheduled_report_name =
                    scheduledReport.scheduled_report_name;
                const reportInOptions = this.findReportInOptions(
                    scheduledReport
                );

                if (reportInOptions) {
                    this.data.report = reportInOptions;
                }

                this.data.export_type = scheduledReport.export_type?.id ?? null;
                this.data.recurring = scheduledReport.recurring?.id ?? null;
                this.data.report_date = this._carbonDateDisplay(
                    scheduledReport.report_date
                );
                this.data.recipients = scheduledReport.recipients.map(
                    recipient => {
                        return {
                            id: recipient.id,
                            fullname: recipient.display_name
                        };
                    }
                );
                this.data.send_type = scheduledReport.send_type?.id ?? null;

                // filter
                this.data.filters = scheduledReport.filters;

                if (scheduledReport.filters) {
                    this.data.date_range = scheduledReport.filters.date_range;
                    this.data.export_scope =
                        scheduledReport.filters.export_scope;
                }

                this.$emit("is-edit-mode", scheduledReport);
            } else {
                this._message("Scheduled report not found");
                this.$router.push({
                    name: "report_dashboard",
                    query: {
                        tab: "scheduled-report"
                    }
                });
                return;
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.cancel-btn {
    background-color: #e1e1e1;
}
.save-btn {
    background-color: #180f29;
    color: white;
}
::v-deep .vue-treeselect__multi-value-item {
    color: black;
    background: #f0f0f0;
    font-size: 1em;
    border-radius: 4px;
    border: 1px solid #cacaca;

    .vue-treeselect__icon.vue-treeselect__value-remove {
        color: gray;

        &:hover {
            color: black;
        }
    }
}
</style>
